import { useEffect, useRef } from 'react'
import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableCell } from '@tiptap/extension-table-cell'
import { COMPONENTS_RICH_TEXT_EDITOR_TEXTS as T } from '../../shared/texts'

// Small WYSIWYG editor for transactional mail bodies. Emits HTML; an empty
// document emits '' so a blank per-customer override stays empty (→ falls back to
// the global default). StarterKit v3 already bundles Link + lists + headings; we
// only add the table extensions on top.
export default function RichTextEditor({ value, onChange }: { value: string, onChange: (html: string) => void }) {
    const lastHtml = useRef(value)

    const editor = useEditor({
        extensions: [
            StarterKit.configure({ link: { openOnClick: false } }),
            Table.configure({ resizable: false }),
            TableRow,
            TableHeader,
            TableCell,
        ],
        content: value || '',
        editorProps: {
            // Schreib-Assistenten (Grammarly/LanguageTool) injizieren eigene
            // Platzhalter + „+"-Buttons in contenteditable-Felder. Hier abschalten,
            // damit der Editor in jedem Browser gleich (sauber) aussieht.
            attributes: {
                'data-gramm': 'false',
                'data-gramm_editor': 'false',
                'data-enable-grammarly': 'false',
                spellcheck: 'false',
            },
        },
        onUpdate: ({ editor }) => {
            const html = editor.isEmpty ? '' : editor.getHTML()
            lastHtml.current = html
            onChange(html)
        },
    })

    // Apply external value changes (record load / reset) without firing onUpdate
    // and without resetting the cursor on every keystroke (compare to last emit).
    useEffect(() => {
        if (!editor) return
        if (value !== lastHtml.current) {
            lastHtml.current = value
            editor.commands.setContent(value || '', { emitUpdate: false })
        }
    }, [value, editor])

    if (!editor) return null

    return (
        <div className="rte">
            <Toolbar editor={editor} />
            <EditorContent editor={editor} className="rte-content" />
        </div>
    )
}

function Toolbar({ editor }: { editor: Editor }) {
    const btn = (active: boolean) => `rte-btn${active ? ' active' : ''}`

    const setLink = () => {
        const prev = editor.getAttributes('link').href as string | undefined
        const url = window.prompt(T.linkPrompt, prev ?? 'https://')
        if (url === null) return
        if (url === '') { editor.chain().focus().unsetLink().run(); return }
        editor.chain().focus().setLink({ href: url }).run()
    }

    const inTable = editor.isActive('table')

    return (
        <div className="rte-toolbar">
            <button type="button" className={btn(editor.isActive('bold'))} onClick={() => editor.chain().focus().toggleBold().run()} title={T.bold}><b>B</b></button>
            <button type="button" className={btn(editor.isActive('italic'))} onClick={() => editor.chain().focus().toggleItalic().run()} title={T.italic}><i>I</i></button>
            <button type="button" className={btn(editor.isActive('heading', { level: 2 }))} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title={T.heading}>H</button>
            <span className="rte-sep" />
            <button type="button" className={btn(editor.isActive('bulletList'))} onClick={() => editor.chain().focus().toggleBulletList().run()} title={T.bulletListTitle}>{T.bulletList}</button>
            <button type="button" className={btn(editor.isActive('orderedList'))} onClick={() => editor.chain().focus().toggleOrderedList().run()} title={T.orderedListTitle}>{T.orderedList}</button>
            <span className="rte-sep" />
            <button type="button" className={btn(editor.isActive('link'))} onClick={setLink} title={T.link}>{T.link}</button>
            <span className="rte-sep" />
            <button type="button" className="rte-btn" onClick={() => editor.chain().focus().insertTable({ rows: 2, cols: 2, withHeaderRow: true }).run()} title={T.insertTableTitle}>{T.insertTable}</button>
            <button type="button" className="rte-btn" disabled={!inTable} onClick={() => editor.chain().focus().addRowAfter().run()} title={T.addRowTitle}>{T.addRow}</button>
            <button type="button" className="rte-btn" disabled={!inTable} onClick={() => editor.chain().focus().addColumnAfter().run()} title={T.addColumnTitle}>{T.addColumn}</button>
            <button type="button" className="rte-btn" disabled={!inTable} onClick={() => editor.chain().focus().deleteRow().run()} title={T.deleteRowTitle}>{T.deleteRow}</button>
            <button type="button" className="rte-btn" disabled={!inTable} onClick={() => editor.chain().focus().deleteColumn().run()} title={T.deleteColumnTitle}>{T.deleteColumn}</button>
            <button type="button" className="rte-btn" disabled={!inTable} onClick={() => editor.chain().focus().deleteTable().run()} title={T.deleteTableTitle}>{T.deleteTable}</button>
        </div>
    )
}
